import React from 'react';
import { Check, AlertCircle } from 'lucide-react';
import clsx from 'clsx';

/**
 * Progress track of a multi-step form.
 *
 * Purely presentational: it holds no step state and knows nothing about what
 * any step contains. The parent owns the position and decides which steps may
 * be jumped to — this only draws it and reports clicks.
 *
 * A step can be in four states at once in the reader's mind, so they are drawn
 * in priority order: the current one always looks current, a step with pending
 * errors is marked over being merely done, and only then does a visited step
 * read as complete.
 *
 * @param {object} props
 * @param {Array<{id: string, label: string}>} props.steps
 * @param {number} props.currentIndex
 * @param {string[]} [props.visitedIds] - Steps already opened; the only ones
 *   that can be jumped back to.
 * @param {string[]} [props.invalidIds] - Steps whose validation is failing.
 * @param {(index: number) => void} [props.onStepClick]
 */
export const StepIndicator = ({
  steps,
  currentIndex,
  visitedIds = [],
  invalidIds = [],
  onStepClick,
}) => (
  <ol className="flex items-start w-full">
    {steps.map((step, index) => {
      const isCurrent = index === currentIndex;
      const isInvalid = invalidIds.includes(step.id);
      const isVisited = visitedIds.includes(step.id);
      const isComplete = isVisited && !isCurrent && !isInvalid && index < currentIndex;
      const isReachable = Boolean(onStepClick) && isVisited && !isCurrent;

      return (
        <li key={step.id} className="flex-1 flex flex-col items-center min-w-0">
          <div className="flex items-center w-full">
            {/* Half-width rails keep the dot centered under its own label. */}
            <div
              className={clsx(
                'h-0.5 flex-1',
                index === 0 ? 'bg-transparent' : index <= currentIndex ? 'bg-ninja-green' : 'bg-border'
              )}
            />

            <button
              type="button"
              onClick={isReachable ? () => onStepClick(index) : undefined}
              disabled={!isReachable}
              aria-current={isCurrent ? 'step' : undefined}
              className={clsx(
                'shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold border-2 transition-all',
                isReachable && 'cursor-pointer hover:scale-110',
                !isReachable && 'cursor-default',
                isCurrent && !isInvalid && 'bg-ninja-green border-ninja-green text-white',
                isCurrent && isInvalid && 'bg-danger border-danger text-white',
                !isCurrent && isInvalid && 'bg-red-50 dark:bg-red-900/20 border-danger text-danger',
                !isCurrent && !isInvalid && isComplete && 'bg-ninja-greenLight border-ninja-green text-ninja-green',
                !isCurrent && !isInvalid && !isComplete && 'bg-white dark:bg-[#1a1a1a] border-border text-text-muted'
              )}
            >
              {isInvalid ? (
                <AlertCircle size={15} />
              ) : isComplete ? (
                <Check size={15} />
              ) : (
                index + 1
              )}
            </button>

            <div
              className={clsx(
                'h-0.5 flex-1',
                index === steps.length - 1
                  ? 'bg-transparent'
                  : index < currentIndex
                    ? 'bg-ninja-green'
                    : 'bg-border'
              )}
            />
          </div>

          <span
            className={clsx(
              'mt-2 text-xs font-semibold text-center px-1 truncate max-w-full',
              isInvalid
                ? 'text-danger'
                : isCurrent
                  ? 'text-ninja-green'
                  : 'text-text-secondary'
            )}
          >
            {step.label}
          </span>
        </li>
      );
    })}
  </ol>
);
